import { Box } from "@mui/material";
import {
	PreviewDocumentDialog,
	type PreviewDocumentDialogHandle,
} from "@repo/legacy/dialogs/PreviewDocumentForwardRefDialog";
import { useDownloadDocumentLazyQuery, useRefreshMonthlyInvoiceStatusesMutation } from "@repo/server-api";
import { type IdWithName } from "@repo/shared/common";
import { useNotification } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import { useCallback, useRef, useState } from "react";

import { MonthlyOverviewDataGrid } from "../components/MonthlyOverviewDataGrid";
import { MonthlyOverviewToolbar } from "../components/MonthlyOverviewToolbar";
import { MonthlyBillingDialog } from "../dialogs/MonthlyBillingDialog";
import { useMonthlyOverviewList } from "../hooks/useMonthlyOverviewList";

import type { MonthlyBillingDialogHandle } from "../dialogs/MonthlyBillingDialog";
import type { MonthlyProjectOverviewItem } from "../types/MonthlyProjectOverviewTypes";
import type { Dayjs } from "dayjs";

export const MonthlyProjectOverviewPage = () => {
	const { t } = useTranslation();
	const { showNotification } = useNotification();

	const list = useMonthlyOverviewList();
	const monthlyBillingRef = useRef<MonthlyBillingDialogHandle | null>(null);
	const previewDocumentRef = useRef<PreviewDocumentDialogHandle | null>(null);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [refreshStatuses] = useRefreshMonthlyInvoiceStatusesMutation();
	const [downloadDocument] = useDownloadDocumentLazyQuery();

	const handleMonthChange = useCallback(
		(newValue: Dayjs | null) => {
			if (newValue) {
				list.setMonth(newValue);
				list.setPaginationModel({ page: 0, pageSize: list.paginationModel.pageSize });
			}
		},
		[list],
	);

	const handleClientChange = useCallback(
		(client: IdWithName | null) => {
			list.setSelectedClient(client);
			list.setPaginationModel({ page: 0, pageSize: list.paginationModel.pageSize });
		},
		[list],
	);

	const handleEmployeeChange = useCallback(
		(employee: IdWithName | null) => {
			list.setSelectedEmployee(employee);
			list.setPaginationModel({ page: 0, pageSize: list.paginationModel.pageSize });
		},
		[list],
	);

	const handleSearchChange = useCallback(
		(value: string) => {
			list.setSearchValue(value);
		},
		[list],
	);

	const handleExcludeEmptyTendersChange = useCallback(
		(exclude: boolean) => {
			list.setExcludeEmptyTenders(exclude);
			list.setPaginationModel({ page: 0, pageSize: list.paginationModel.pageSize });
		},
		[list],
	);

	const handleExcludeFullyInvoicedChange = useCallback(
		(exclude: boolean) => {
			list.setExcludeFullyInvoiced(exclude);
			list.setPaginationModel({ page: 0, pageSize: list.paginationModel.pageSize });
		},
		[list],
	);

	const handleGroupByClientChange = useCallback(
		(group: boolean) => {
			list.setGroupByClient(group);
			list.setPaginationModel({ page: 0, pageSize: list.paginationModel.pageSize });
		},
		[list],
	);

	const handlePaginationModelChange = useCallback(
		(model: { page: number; pageSize: number }) => {
			list.setPaginationModel(model);
		},
		[list],
	);

	const handleCreateMonthlyBilling = useCallback(
		(item: MonthlyProjectOverviewItem) => {
			if (!item.projectId) return;

			monthlyBillingRef.current?.show({
				projectId: item.projectId,
				projectName: item.project?.name ?? item.projectName ?? t("unknown-project"),
				month: list.month.format("YYYY-MM-DD"),
				approvedTimesheetCount: Math.ceil(item.approvedMinutes / 60), // Estimate based on hours
				projectType: item.projectType,
				onSuccess: () => {
					list.refresh();
				},
			});
		},
		[list, t],
	);

	const handlePreviewDocument = useCallback(
		async (documentId: string) => {
			try {
				const result = await downloadDocument({
					variables: { id: documentId, shouldDownload: false },
				});
				const url = result.data?.downloadDocument;
				if (url) {
					previewDocumentRef.current?.show({ url });
				}
			} catch (error) {
				const message = error instanceof Error ? error.message : String(error);
				showNotification(message, "error");
			}
		},
		[downloadDocument, showNotification],
	);

	const handleRefreshStatuses = useCallback(async () => {
		setIsRefreshing(true);
		try {
			const result = await refreshStatuses({
				variables: { month: list.month.format("YYYY-MM-DD") },
			});

			const data = result.data?.refreshMonthlyInvoiceStatuses;
			if (data?.errors && data.errors.length > 0) {
				for (const error of data.errors) {
					showNotification(error.message, "error");
				}
			} else if (data?.refreshMonthlyInvoiceStatusesResult) {
				const { updatedInvoices, updatedCreditNotes } = data.refreshMonthlyInvoiceStatusesResult;
				if (updatedInvoices === 0 && updatedCreditNotes === 0) {
					showNotification(t("refresh-invoice-statuses-no-changes"), "info");
				} else {
					showNotification(
						t("refresh-invoice-statuses-success", {
							invoices: updatedInvoices,
							creditNotes: updatedCreditNotes,
						}),
						"success",
					);
				}
				list.refresh();
			}
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error);
			showNotification(message, "error");
		} finally {
			setIsRefreshing(false);
		}
	}, [refreshStatuses, list, showNotification, t]);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "max(500px, calc(100vh - 120px))",
				overflow: "hidden",
			}}>
			<MonthlyBillingDialog ref={monthlyBillingRef} />
			<PreviewDocumentDialog ref={previewDocumentRef} />

			<h3 className="purpleHeader" style={{ margin: "0 0 16px 0", flexShrink: 0 }}>
				{t("monthly-project-overview")}
			</h3>

			<MonthlyOverviewToolbar
				selectedMonth={list.month}
				onMonthChange={handleMonthChange}
				selectedClient={list.selectedClient}
				onClientChange={handleClientChange}
				selectedEmployee={list.selectedEmployee}
				onEmployeeChange={handleEmployeeChange}
				searchValue={list.searchValue}
				onSearchChange={handleSearchChange}
				excludeEmptyTenders={list.excludeEmptyTenders}
				onExcludeEmptyTendersChange={handleExcludeEmptyTendersChange}
				excludeFullyInvoiced={list.excludeFullyInvoiced}
				onExcludeFullyInvoicedChange={handleExcludeFullyInvoicedChange}
				groupByClient={list.groupByClient}
				onGroupByClientChange={handleGroupByClientChange}
				onRefreshStatuses={handleRefreshStatuses}
				isRefreshing={isRefreshing}
			/>

			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					"& .MuiDataGrid-root": { height: "100% !important" },
					"& .MuiDataGrid-main": { minHeight: 0 },
				}}>
				<MonthlyOverviewDataGrid
					rows={list.rows}
					subRows={list.subRows}
					rowCount={list.rowCount}
					isLoading={list.isLoading}
					paginationModel={list.paginationModel}
					onPaginationModelChange={handlePaginationModelChange}
					onCreateMonthlyBilling={handleCreateMonthlyBilling}
					onPreviewDocument={handlePreviewDocument}
					onExpandRow={list.expandRow}
					onExpandAll={list.expandAll}
					groupByClient={list.groupByClient}
					matchedProjectIds={list.matchedProjectIds}
					isSearchMode={!!list.debouncedSearchValue}
				/>
			</Box>
		</Box>
	);
};
